"use client"

import type React from "react"
import { useState, useMemo, useRef, useEffect, useCallback } from "react"
import { X, Plus, Loader2, Save, Sparkles } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/hooks/use-toast"
import { CRM_API_BASE } from "@/lib/config"
import { httpJson } from "@/lib/http"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

interface Category {
  id: number
  text: string
  slug?: string
}

interface AddOrganizationModalProps {
  open: boolean
  onClose: () => void
  onSuccess?: (organizationId: number) => void
}

function extractDomainAndHost(url: string): { domain: string; host: string } {
  if (!url?.trim()) return { domain: "", host: "" }
  const trimmed = url.trim()
  try {
    const urlObj = new URL(trimmed.startsWith("http") ? trimmed : `https://${trimmed}`)
    const host = urlObj.hostname
    const domain = host.replace(/^www\./, "")
    return { domain, host }
  } catch {
    const withoutProtocol = trimmed.replace(/^https?:\/\//, "").split("/")[0] || ""
    const domain = withoutProtocol.replace(/^www\./, "")
    return { domain, host: withoutProtocol || domain }
  }
}

const SOCIAL_MEDIA_CONFIG: Array<{ key: "facebook" | "twitter" | "linkedin" | "instagram" | "youtube"; slug: string; text: string; baseUrl: string }> = [
  { key: "facebook", slug: "facebook", text: "Facebook", baseUrl: "https://www.facebook.com/" },
  { key: "twitter", slug: "twitter", text: "Twitter/X", baseUrl: "https://x.com/" },
  { key: "linkedin", slug: "linkedin", text: "LinkedIn", baseUrl: "https://www.linkedin.com/company/" },
  { key: "instagram", slug: "instagram", text: "Instagram", baseUrl: "https://www.instagram.com/" },
  { key: "youtube", slug: "youtube", text: "YouTube", baseUrl: "https://www.youtube.com/" },
]

const VALIDATION_MESSAGES: Record<string, Record<string, string>> = {
  email: {
    "org with this email already exists.": "This email is already associated with another organization.",
  },
  addresses: {
    "This list may not be empty.": "Please add at least one address.",
  },
}

const OPTIONAL_FIELDS = ["addresses", "target_audience"]

function parseValidationErrors(err: unknown): Record<string, string> {
  const msg = err instanceof Error ? err.message : ""
  const result: Record<string, string> = {}
  try {
    const data = typeof msg === "string" ? JSON.parse(msg) : null
    if (data && typeof data === "object" && !Array.isArray(data)) {
      for (const [field, messages] of Object.entries(data)) {
        if (OPTIONAL_FIELDS.includes(field)) continue
        const arr = Array.isArray(messages) ? messages : [messages]
        const fieldMap = VALIDATION_MESSAGES[field]
        const friendlyMessages: string[] = []
        for (const m of arr) {
          const text = String(m)
          const friendly = fieldMap?.[text] ?? text
          if (friendly && !friendlyMessages.includes(friendly)) friendlyMessages.push(friendly)
        }
        if (friendlyMessages.length > 0) result[field] = friendlyMessages[0]
      }
    }
  } catch {
    /* not JSON */
  }
  return result
}

function parseSocialMediaValue(
  value: string,
  baseUrl: string
): { url: string; username: string } {
  const trimmed = value.trim()
  if (!trimmed) return { url: "", username: "" }
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    const url = trimmed
    const parts = trimmed.split("/")
    let username = parts[parts.length - 1] || parts[parts.length - 2] || ""
    username = username.split("?")[0].split("#")[0]
    return { url, username }
  }
  if (trimmed.includes("/") || trimmed.includes(".")) {
    const url = trimmed.startsWith("http") ? trimmed : `https://${trimmed}`
    const parts = trimmed.split("/")
    let username = parts[parts.length - 1] || ""
    username = username.split("?")[0].split("#")[0]
    return { url, username }
  }
  return { url: `${baseUrl}${trimmed}`, username: trimmed }
}

export function AddOrganizationModal({ open, onClose, onSuccess }: AddOrganizationModalProps) {
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState({
    website_url: "",
    name: "",
    logo: null as File | null,
    about: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip: "",
    target_audience: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    title: "",
    facebook: "",
    twitter: "",
    linkedin: "",
    instagram: "",
    youtube: "",
  })
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState("")
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [errorFields, setErrorFields] = useState<string[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [loadingCategories, setLoadingCategories] = useState(false)
  const [categorySearch, setCategorySearch] = useState("")
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([])
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false)
  const categoryDropdownRef = useRef<HTMLDivElement>(null)

  const resetForm = useCallback(() => {
    setForm({
      website_url: "",
      name: "",
      logo: null,
      about: "",
      address_line1: "",
      address_line2: "",
      city: "",
      state: "",
      zip: "",
      target_audience: "",
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      title: "",
      facebook: "",
      twitter: "",
      linkedin: "",
      instagram: "",
      youtube: "",
    })
    setError("")
    setFieldErrors({})
    setErrorFields([])
    setSelectedCategories([])
    setCategorySearch("")
    if (fileInputRef.current) fileInputRef.current.value = ""
  }, [])

  useEffect(() => {
    if (!open) {
      resetForm()
    }
  }, [open, resetForm])

  const fetchCategories = useCallback(async (searchTerm: string) => {
    try {
      setLoadingCategories(true)
      const url = searchTerm.trim()
        ? `${CRM_API_BASE}/category/?q=${encodeURIComponent(searchTerm.trim())}&limit=20`
        : `${CRM_API_BASE}/category/?limit=20`
      const res = await httpJson<{ results?: Category[] }>(url)
      setCategories(res.results ?? [])
    } catch {
      setCategories([])
    } finally {
      setLoadingCategories(false)
    }
  }, [])

  useEffect(() => {
    if (open) {
      fetchCategories(categorySearch)
    }
  }, [open, categorySearch, fetchCategories])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target as Node)) {
        setShowCategoryDropdown(false)
      }
    }
    if (showCategoryDropdown) document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [showCategoryDropdown])

  const formValidation = useMemo(() => {
    const required: Record<string, string | undefined> = {
      website_url: form.website_url?.trim(),
      name: form.name?.trim(),
      city: form.city?.trim(),
      state: form.state?.trim(),
      first_name: form.first_name?.trim(),
      last_name: form.last_name?.trim(),
      email: form.email?.trim(),
    }
    const missingFields = Object.entries(required)
      .filter(([, v]) => !v)
      .map(([k]) => k)
    return { isValid: missingFields.length === 0, missingFields }
  }, [form])

  const clearFieldError = (apiFields: string[]) => {
    setFieldErrors((prev) => {
      const next = { ...prev }
      for (const k of apiFields) delete next[k]
      return next
    })
  }

  const handleChange = (field: keyof typeof form, value: string | File | null) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (error) setError("")
    if (errorFields.length > 0) setErrorFields([])
    const addressFields = ["address_line1", "address_line2", "city", "state", "zip"]
    const apiFields = addressFields.includes(field) ? ["addresses"] : field === "website_url" ? ["url", "domain"] : [field]
    clearFieldError(apiFields)
  }

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null
    if (file) {
      if (!file.type.startsWith("image/")) {
        setError("Please select an image file (PNG, JPG recommended)")
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        setError("Image size must be less than 5MB")
        return
      }
      handleChange("logo", file)
    } else {
      handleChange("logo", null)
    }
  }

  const addCategory = (cat: Category) => {
    if (!selectedCategories.some((c) => c.id === cat.id)) {
      setSelectedCategories((prev) => [...prev, cat])
    }
    setCategorySearch("")
    setShowCategoryDropdown(false)
  }

  const removeCategory = (id: number) => {
    setSelectedCategories((prev) => prev.filter((c) => c.id !== id))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (loading) return

    if (!formValidation.isValid) {
      setError("Please fill in all required fields")
      setErrorFields(formValidation.missingFields)
      return
    }

    setLoading(true)
    setError("")
    setFieldErrors({})
    setErrorFields([])

    try {
      const token = typeof window !== "undefined" ? localStorage.getItem("auth_token") : null
      if (!token) throw new Error("Authentication token not found")

      const addressIds: number[] = []
      const hasAddressData =
        form.address_line1?.trim() ||
        form.address_line2?.trim() ||
        form.city?.trim() ||
        form.state?.trim() ||
        form.zip?.trim()
      if (hasAddressData) {
        const addrRes = await httpJson<{ id: number }>(`${CRM_API_BASE}/address/`, {
          method: "POST",
          body: JSON.stringify({
            ...(form.address_line1?.trim() && { line_1: form.address_line1.trim() }),
            ...(form.address_line2?.trim() && { line_2: form.address_line2.trim() }),
            ...(form.city?.trim() && { city: form.city.trim() }),
            ...(form.state?.trim() && { state: form.state.trim() }),
            ...(form.zip?.trim() && { zip_code: form.zip.trim() }),
            tags: ["primary"],
          }),
        })
        addressIds.push(addrRes.id)
      }

      const socialMediaIds: number[] = []
      for (const config of SOCIAL_MEDIA_CONFIG) {
        const value = form[config.key]?.trim()
        if (!value) continue
        const { url, username } = parseSocialMediaValue(value, config.baseUrl)
        if (!url) continue
        const smRes = await httpJson<{ id: number }>(`${CRM_API_BASE}/social_media/`, {
          method: "POST",
          body: JSON.stringify({
            slug: config.slug,
            text: config.text,
            username,
            url,
          }),
        })
        socialMediaIds.push(smRes.id)
      }

      const { domain, host } = extractDomainAndHost(form.website_url)
      const url = form.website_url.trim().startsWith("http")
        ? form.website_url.trim()
        : `https://${form.website_url.trim()}`

      const pad = (n: number) => String(n).padStart(2, "0")
      const now = new Date()
      const dateAdded = `${pad(now.getUTCFullYear())}-${pad(now.getUTCMonth() + 1)}-${pad(now.getUTCDate())} ${pad(now.getUTCHours())}:${pad(now.getUTCMinutes())}:${pad(now.getUTCSeconds())}`

      const payload: Record<string, unknown> = {
        url,
        domain: domain || undefined,
        host: host || undefined,
        name: form.name.trim(),
        contact: `${form.first_name.trim()} ${form.last_name.trim()}`.trim(),
        name_first: form.first_name.trim(),
        name_last: form.last_name.trim(),
        email: form.email.trim(),
        date_added: dateAdded,
      }

      if (form.about?.trim()) payload.about = form.about.trim()
      if (form.phone?.trim()) payload.phone = form.phone.trim()
      if (form.title?.trim()) payload.contact_title = form.title.trim()
      if (form.target_audience?.trim()) payload.target_audience = form.target_audience.trim()
      payload.addresses = addressIds
      if (socialMediaIds.length > 0) payload.social_medias = socialMediaIds
      if (selectedCategories.length > 0) {
        payload.categories = selectedCategories.map((c) => c.id)
      }

      const response = await httpJson<{ id: number }>(`${CRM_API_BASE}/org/`, {
        method: "POST",
        body: JSON.stringify(payload),
      })

      if (form.logo) {
        const formData = new FormData()
        formData.append("logo", form.logo)
        const logoRes = await fetch(`${CRM_API_BASE}/org/${response.id}/`, {
          method: "PATCH",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        })
        if (!logoRes.ok) {
          toast({
            title: "Warning",
            description: "Organization created, but logo upload failed.",
            variant: "destructive",
          })
        }
      }

      toast({
        title: "Success",
        description: "Organization created successfully",
      })
      onSuccess?.(response.id)
      onClose()
    } catch (err: unknown) {
      const errors = parseValidationErrors(err)
      setFieldErrors(errors)
      const firstMsg = Object.values(errors)[0] ?? (err instanceof Error ? err.message : "Failed to create organization")
      toast({
        title: "Cannot create organization",
        description: firstMsg,
      })
    } finally {
      setLoading(false)
    }
  }

  const labelClass = "mb-1.5 block text-sm font-medium text-foreground"

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-w-3xl w-[95vw] min-w-[560px] max-h-[90vh] overflow-y-auto p-0 gap-0 rounded-lg border border-gray-300 shadow-xl [&>button]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="sticky top-0 z-10 shrink-0 border-b border-gray-300 bg-white px-6 py-4 flex flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-xl font-semibold text-gray-900">Add Organization</DialogTitle>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700 -m-1.5"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col min-h-0">
          <div className="flex flex-col gap-6 p-6 overflow-y-auto flex-1">
            <section className="rounded-lg border border-gray-300 bg-white p-5">
              <h2 className="mb-5 text-lg font-semibold text-gray-900">Organization Information</h2>

              <div className="mb-4">
                <label className={labelClass} htmlFor="website_url">
                  Website URL <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <Input
                    id="website_url"
                    type="text"
                    inputMode="url"
                    value={form.website_url}
                    onChange={(e) => handleChange("website_url", e.target.value)}
                    placeholder="example.com or https://example.com"
                    className={`flex-1 ${errorFields.includes("website_url") || fieldErrors.url || fieldErrors.domain ? "border-red-500" : ""}`}
                  />
                  <Button type="button" variant="secondary" className="gap-2 shrink-0" disabled>
                    <Sparkles className="size-4" />
                    Auto-fill
                  </Button>
                </div>
                {(fieldErrors.url || fieldErrors.domain) && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.url ?? fieldErrors.domain}</p>
                )}
              </div>

              <div className="mb-4 grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass} htmlFor="name">
                    Organization Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="name"
                    type="text"
                    value={form.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder="Enter organization name"
                    className={errorFields.includes("name") || fieldErrors.name ? "border-red-500" : ""}
                  />
                  {fieldErrors.name && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors.name}</p>
                  )}
                </div>
                <div>
                  <label className={labelClass} htmlFor="logo">
                    Company Logo
                  </label>
                  <input
                    ref={fileInputRef}
                    id="logo"
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml"
                    onChange={handleLogoChange}
                    className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm file:mr-3 file:border-0 file:bg-transparent file:text-sm file:font-medium text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
                  />
                  <p className="mt-1 text-xs text-gray-500">Upload company logo (PNG, JPG, or SVG recommended)</p>
                </div>
              </div>

              <div className="mb-4">
                <label className={labelClass} htmlFor="about">About</label>
                <Textarea
                  id="about"
                  rows={4}
                  value={form.about}
                  onChange={(e) => handleChange("about", e.target.value)}
                  placeholder="Enter organization description"
                  className="w-full resize-y rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
                />
              </div>

              <div className="mb-4">
                <label className={labelClass} htmlFor="address_line1">Address Line 1</label>
                <Input
                  id="address_line1"
                  type="text"
                  value={form.address_line1}
                  onChange={(e) => handleChange("address_line1", e.target.value)}
                  placeholder="Street address"
                />
              </div>

              <div className="mb-4">
                <label className={labelClass} htmlFor="address_line2">Address Line 2</label>
                <Input
                  id="address_line2"
                  type="text"
                  value={form.address_line2}
                  onChange={(e) => handleChange("address_line2", e.target.value)}
                  placeholder="Apartment, suite, etc. (optional)"
                />
              </div>

              <div className="mb-4 grid grid-cols-3 gap-4">
                <div>
                  <label className={labelClass} htmlFor="city">City <span className="text-red-500">*</span></label>
                  <Input
                    id="city"
                    type="text"
                    value={form.city}
                    onChange={(e) => handleChange("city", e.target.value)}
                    placeholder="City"
                    className={errorFields.includes("city") ? "border-red-500" : ""}
                  />
                  {errorFields.includes("city") && (
                    <p className="mt-1 text-sm text-red-600">City is required.</p>
                  )}
                </div>
                <div>
                  <label className={labelClass} htmlFor="state">State <span className="text-red-500">*</span></label>
                  <Input
                    id="state"
                    type="text"
                    value={form.state}
                    onChange={(e) => handleChange("state", e.target.value)}
                    placeholder="State"
                    className={errorFields.includes("state") ? "border-red-500" : ""}
                  />
                  {errorFields.includes("state") && (
                    <p className="mt-1 text-sm text-red-600">State is required.</p>
                  )}
                </div>
                <div>
                  <label className={labelClass} htmlFor="zip">Zip Code</label>
                  <Input
                    id="zip"
                    type="text"
                    value={form.zip}
                    onChange={(e) => handleChange("zip", e.target.value)}
                    placeholder="ZIP"
                  />
                </div>
              </div>

              <div>
                <label className={labelClass} htmlFor="target_audience">Target Audience</label>
                <Input
                  id="target_audience"
                  type="text"
                  value={form.target_audience}
                  onChange={(e) => handleChange("target_audience", e.target.value)}
                  placeholder="e.g., children, kids, youth"
                />
              </div>
            </section>

            <section className="rounded-lg border border-gray-300 bg-white p-5" ref={categoryDropdownRef}>
              <h2 className="mb-5 text-lg font-semibold text-gray-900">Categories</h2>
              <div>
                <label className={labelClass}>Organization Categories</label>
                <div className="relative flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      type="text"
                      placeholder="Add a category (e.g., Education, Environment)"
                      value={categorySearch}
                      onChange={(e) => {
                        setCategorySearch(e.target.value)
                        setShowCategoryDropdown(true)
                      }}
                      onFocus={() => setShowCategoryDropdown(true)}
                    />
                    {showCategoryDropdown && (
                      <div className="absolute left-0 top-full z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg">
                        {loadingCategories ? (
                          <div className="p-3 text-sm text-gray-500">Loading...</div>
                        ) : categories.length === 0 ? (
                          <div className="p-3 text-sm text-gray-500">No categories found</div>
                        ) : (
                          categories
                            .filter((c) => !selectedCategories.some((s) => s.id === c.id))
                            .filter((c) => !categorySearch.trim() || c.text.toLowerCase().includes(categorySearch.toLowerCase()))
                            .map((c) => (
                              <button
                                key={c.id}
                                type="button"
                                className="w-full px-3 py-2 text-left text-sm text-gray-900 hover:bg-gray-100"
                                onClick={() => addCategory(c)}
                              >
                                {c.text}
                              </button>
                            ))
                        )}
                      </div>
                    )}
                  </div>
                  <Button
                    type="button"
                    size="icon"
                    onClick={() => {
                      const first = categories.find((c) => !selectedCategories.some((s) => s.id === c.id) && (!categorySearch.trim() || c.text.toLowerCase().includes(categorySearch.toLowerCase())))
                      if (first) addCategory(first)
                    }}
                    className="shrink-0"
                  >
                    <Plus className="size-4" />
                  </Button>
                </div>
                {selectedCategories.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {selectedCategories.map((c) => (
                      <span
                        key={c.id}
                        className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700"
                      >
                        {c.text}
                        <button
                          type="button"
                          onClick={() => removeCategory(c.id)}
                          className="rounded-full p-0.5 hover:bg-gray-200"
                        >
                          <X className="size-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </section>

            <section className="rounded-lg border border-gray-300 bg-white p-5">
              <h2 className="mb-5 text-lg font-semibold text-gray-900">Social Media Links</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass} htmlFor="facebook">Facebook</label>
                  <Input
                    id="facebook"
                    type="url"
                    value={form.facebook}
                    onChange={(e) => handleChange("facebook", e.target.value)}
                    placeholder="https://www.facebook.com/..."
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="twitter">Twitter/X</label>
                  <Input
                    id="twitter"
                    type="url"
                    value={form.twitter}
                    onChange={(e) => handleChange("twitter", e.target.value)}
                    placeholder="https://x.com/..."
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="linkedin">LinkedIn</label>
                  <Input
                    id="linkedin"
                    type="url"
                    value={form.linkedin}
                    onChange={(e) => handleChange("linkedin", e.target.value)}
                    placeholder="https://www.linkedin.com/company/..."
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="instagram">Instagram</label>
                  <Input
                    id="instagram"
                    type="url"
                    value={form.instagram}
                    onChange={(e) => handleChange("instagram", e.target.value)}
                    placeholder="https://www.instagram.com/..."
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="youtube">YouTube</label>
                  <Input
                    id="youtube"
                    type="url"
                    value={form.youtube}
                    onChange={(e) => handleChange("youtube", e.target.value)}
                    placeholder="https://www.youtube.com/channel/..."
                  />
                </div>
              </div>
            </section>

            <section className="rounded-lg border border-gray-300 bg-white p-5">
              <h2 className="mb-5 text-lg font-semibold text-gray-900">Primary Contact</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass} htmlFor="first_name">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="first_name"
                    type="text"
                    value={form.first_name}
                    onChange={(e) => handleChange("first_name", e.target.value)}
                    placeholder="First name"
                    className={errorFields.includes("first_name") ? "border-red-500" : ""}
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="last_name">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="last_name"
                    type="text"
                    value={form.last_name}
                    onChange={(e) => handleChange("last_name", e.target.value)}
                    placeholder="Last name"
                    className={errorFields.includes("last_name") ? "border-red-500" : ""}
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="email">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="email@example.com"
                    className={errorFields.includes("email") || fieldErrors.email ? "border-red-500" : ""}
                  />
                  {fieldErrors.email && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>
                  )}
                </div>
                <div>
                  <label className={labelClass} htmlFor="phone">Phone</label>
                  <Input
                    id="phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    placeholder="(555) 555-5555"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className={labelClass} htmlFor="title">Title</label>
                <Input
                  id="title"
                  type="text"
                  value={form.title}
                  onChange={(e) => handleChange("title", e.target.value)}
                  placeholder="Job title"
                />
              </div>
            </section>
          </div>

          <div className="sticky bottom-0 shrink-0 border-t border-gray-300 bg-white px-6 py-4">
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading || !formValidation.isValid} className="gap-2 bg-[#1C882D] hover:bg-[#259635] text-white">
                {loading ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="size-4" />
                    Save Record
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
